import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import { Button, Paragraph1, Paragraph2, Divider } from 'inpock-ui-react';
import { findProvider } from '@extractus/oembed-extractor';
import axios from 'axios';

import { Icon, Rating, CampaignReviews, EmptyCampaignReviews } from '@/components';
import { Campaign, ReviewData, ReviewSortCriteria } from '@/apis';

import * as S from './styled';
interface CampaignDescription {
  campaign?: Campaign;
  fold: boolean;
  setFold: Dispatch<SetStateAction<boolean>>;
  isFold: boolean;
  setIsFold: Dispatch<SetStateAction<boolean>>;
  menu: string[];
  isTab: string;
  setTab: Dispatch<SetStateAction<string>>;
  csChannel: string;
}

type CampaignInfo = Pick<
  CampaignDescription,
  'campaign' | 'fold' | 'setFold' | 'isFold' | 'setIsFold'
>;

interface SellerInfo {
  label: string;
  value: string | number | null | undefined;
}

const redirectToValidURL = (url: string) => {
  // 정규 표현식을 사용하여 URL이 "http://" 또는 "https://"로 시작하는지 확인
  const urlPattern = /^https?:\/\//i;

  if (!urlPattern.test(url)) {
    // URL에 "http://"를 추가하여 새 URL을 생성
    const newURL = 'http://' + url;
    // 현재 페이지를 새 URL로 리다이렉트
    window.location.href = newURL;
  } else {
    window.location.href = url;
  }
};

const REVIEW_SORT_CRITERIA: ReviewSortCriteria[] = [
  { label: '평점순', value: 'rating' },
  { label: '최신순', value: 'created_at' },
];

// 상품 정보 영역
function CampaignInfoSection(props: CampaignInfo) {
  const { campaign, fold, isFold, setIsFold } = props;
  const [html, setHtml] = useState<string | null>(null);
  const oembedRegex = /<oembed[^>]*>/g;
  const oembedMatch = campaign?.description?.match(oembedRegex);

  useEffect(() => {
    let originDescription = campaign?.description as string;

    const getExtract = async (url: string) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const endpoint = findProvider(url) as any;
        if (!endpoint) return;
        const instance = axios.create();

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data }: any = await instance({
          method: 'GET',
          baseURL: endpoint?.endpoint.replace('{format}', 'json') + '?url=' + url,
        });

        return data;
      } catch (error) {
        return error;
      }
    };

    if (oembedMatch) {
      oembedMatch.forEach((oembed) => {
        const oembedUrl = oembed.match(/url="([^"]*)"/);
        if (oembedUrl) {
          getExtract(oembedUrl[1])
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .then((data: any) => {
              if (data?.html)
                originDescription = originDescription.replace(oembed, data?.html ?? '');
              setHtml(originDescription);
            })
            .catch(() => {
              setHtml(originDescription);
            });
        }
      });
    } else {
      setHtml(originDescription);
    }
  }, [campaign?.description]);

  return (
    <>
      {html && (
        <S.FoldableArticle
          className="summernote-mobile-view description"
          fold={fold && isFold}
          dangerouslySetInnerHTML={{
            __html: campaign?.description ? html : '<p>상품 상세정보가 없습니다</p>',
          }}
        />
      )}
      {fold && (
        <Button
          className="mb-16"
          size="medium"
          type="line"
          fullWidth
          onClick={() => setIsFold((prev) => !prev)}
        >
          <Paragraph2 className="mr-12">{isFold ? '상품정보 펼치기' : '상품정보 접기'}</Paragraph2>
          <Icon icon={isFold ? 'ArrowDownLight' : 'ArrowUpLight'} size={12}></Icon>
        </Button>
      )}
    </>
  );
}

// 상품 리뷰 영역
function CampaignReviewSection({
  campaignId,
  reviewData,
}: {
  campaignId: number;
  reviewData?: ReviewData;
}) {
  const [sortCriteria, setSortCriteria] = useState<ReviewSortCriteria>(REVIEW_SORT_CRITERIA[0]);
  if (!reviewData || reviewData.counts === 0) {
    return <EmptyCampaignReviews />;
  }
  const { counts, avr_rating } = reviewData as ReviewData;

  return (
    <div className="pt-6 pb-30 pl-12 pr-12">
      <div className="d-flex flex-column">
        <Paragraph1 className="mb-8">
          리뷰 <span className="text-orange">{counts}</span>
        </Paragraph1>
        <Rating rating={avr_rating} size={18} readOnly ratingInfo={{ avgRating: avr_rating }} />
      </div>
      <Divider spacing={[20, 20]} />
      <S.ReviewTabs>
        {REVIEW_SORT_CRITERIA.map((v, idx) => (
          <S.ReviewTab
            key={idx}
            active={sortCriteria.value === v.value}
            onClick={() => {
              setSortCriteria(v);
            }}
          >
            <Paragraph2 fontWeight="regular">{v.label}</Paragraph2>
          </S.ReviewTab>
        ))}
      </S.ReviewTabs>
      <CampaignReviews campaignId={campaignId} criteria={sortCriteria} />
    </div>
  );
}

export function ProductDescription(props: CampaignDescription) {
  const { campaign, menu, isTab, setTab } = props;
  const descriptionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!descriptionRef.current) return;
    const { top, bottom } =
      descriptionRef.current && descriptionRef.current.getBoundingClientRect();
    props.setFold(bottom - top >= 1400 ? true : false);
  }, [props, campaign?.description]);

  useEffect(() => {
    if (!descriptionRef.current) return;

    descriptionRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [setTab, isTab]);

  return (
    <S.Container ref={descriptionRef}>
      <S.Tabs>
        {menu.map((menu) => {
          return (
            <S.Tab
              key={menu}
              active={menu == isTab}
              onClick={() => {
                setTab(menu);
              }}
            >
              <Paragraph1 fontWeight="regular">{menu}</Paragraph1>
            </S.Tab>
          );
        })}
      </S.Tabs>
      {isTab === menu[0] ? (
        <CampaignInfoSection
          campaign={campaign}
          fold={props.fold}
          setFold={props.setFold}
          isFold={props.isFold}
          setIsFold={props.setIsFold}
        />
      ) : (
        <CampaignReviewSection
          campaignId={campaign?.id as number}
          reviewData={campaign?.review_data}
        />
      )}
    </S.Container>
  );
}
